import React, { useState, useEffect } from 'react';
import {
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  TextField,
  TablePagination,
  TableSortLabel,
  Box,
  InputAdornment,
  Chip,
  Card,
  CardContent,
  Grid,
  useTheme,
  useMediaQuery,
  Skeleton,
  Alert,
  Tooltip,
  Fab,
  Avatar
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  Work as WorkIcon,
  AttachMoney as MoneyIcon,
  Description as DescriptionIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useEmpresa } from '../context/EmpresaContext';
import config from '../config';

function ServiciosAdmin() {
  const [servicios, setServicios] = useState([]);
  const [filteredServicios, setFilteredServicios] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [orderBy, setOrderBy] = useState('nombre_servicio');
  const [order, setOrder] = useState('asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const { user } = useAuth();
  const { simboloMoneda } = useEmpresa();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    fetchServicios();
  }, []);

  useEffect(() => {
    const filterAndSortServicios = () => {
      let filtered = [...servicios];
      
      if (searchTerm) {
        filtered = filtered.filter(servicio =>
          Object.values(servicio)
            .join(' ')
            .toLowerCase()
            .includes(searchTerm.toLowerCase())
        );
      }

      filtered.sort((a, b) => {
        const isAsc = order === 'asc';
        if (a[orderBy] < b[orderBy]) return isAsc ? -1 : 1;
        if (a[orderBy] > b[orderBy]) return isAsc ? 1 : -1;
        return 0;
      });

      setFilteredServicios(filtered);
      setPage(0);
    };

    filterAndSortServicios();
  }, [servicios, searchTerm, order, orderBy]);

  const fetchServicios = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${config.API_BASE_URL}/api/servicios`, {
        headers: { Authorization: `Bearer ${user.token}` }
      });
      setServicios(response.data);
      setError(null);
    } catch (error) {
      console.error('Error al obtener los servicios:', error);
      setError('Error al cargar los servicios. Por favor, inténtelo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, nombre) => {
    if (window.confirm(`¿Estás seguro de eliminar el servicio "${nombre}"?`)) {
      try {
        await axios.delete(`${config.API_BASE_URL}/api/servicios/${id}`, {
          headers: { Authorization: `Bearer ${user.token}` }
        });
        fetchServicios();
      } catch (error) {
        console.error('Error al eliminar el servicio:', error);
        setError('Error al eliminar el servicio.');
      }
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  const getServiceIcon = (servicio) => {
    const nombre = servicio.nombre_servicio?.toLowerCase() || '';
    if (nombre.includes('limpieza')) return '🧹';
    if (nombre.includes('mantenimiento')) return '🔧';
    if (nombre.includes('seguridad')) return '🛡️';
    if (nombre.includes('jardinería')) return '🌿';
    if (nombre.includes('cocina')) return '👨‍🍳';
    return '⚙️';
  };

  const formatPrice = (price) => {
    return `${simboloMoneda} ${parseFloat(price || 0).toFixed(2)}`;
  };

  // Vista móvil con tarjetas
  const MobileServiceCard = ({ servicio }) => (
    <Card 
      sx={{ 
        mb: 2,
        borderRadius: 3,
        border: '1px solid rgba(26, 35, 126, 0.1)',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(26, 35, 126, 0.15)'
        }
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Box
            sx={{
              width: 50,
              height: 50,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #4FC3F7 0%, #29B6F6 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '1.5rem',
              mr: 2
            }}
          >
            {getServiceIcon(servicio)}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#1A237E', mb: 0.5 }}>
              {servicio.nombre_servicio}
            </Typography>
            <Chip 
              label={formatPrice(servicio.precio)}
              icon={<MoneyIcon sx={{ fontSize: 16 }} />}
              sx={{ 
                backgroundColor: 'rgba(76, 175, 80, 0.1)',
                color: '#4CAF50',
                fontWeight: 'bold'
              }}
            />
          </Box>
        </Box>

        {servicio.descripcion_servicio && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ 
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden'
            }}>
              {servicio.descripcion_servicio}
            </Typography>
          </Box>
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2 }}>
          <Tooltip title="Editar servicio">
            <IconButton
              size="small"
              onClick={() => navigate(`/home/servicios/editar/${servicio.id_servicio}`)}
              sx={{ 
                bgcolor: 'primary.light', 
                color: 'white',
                '&:hover': { bgcolor: 'primary.main' }
              }}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar servicio">
            <IconButton
              size="small"
              onClick={() => handleDelete(servicio.id_servicio, servicio.nombre_servicio)}
              sx={{ 
                bgcolor: 'error.light', 
                color: 'white',
                '&:hover': { bgcolor: 'error.main' }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );

  // Estadísticas rápidas
  const estadisticas = {
    total: servicios.length,
    promedio: servicios.length > 0 ? servicios.reduce((sum, s) => sum + parseFloat(s.precio || 0), 0) / servicios.length : 0,
    masCaroe: servicios.length > 0 ? Math.max(...servicios.map(s => parseFloat(s.precio || 0))) : 0,
    masBarato: servicios.length > 0 ? Math.min(...servicios.map(s => parseFloat(s.precio || 0))) : 0
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', p: { xs: 2, sm: 3, md: 4 } }}>
        <Skeleton variant="text" width="60%" height={60} sx={{ mb: 3 }} />
        <Skeleton variant="rectangular" width="100%" height={400} sx={{ borderRadius: 3 }} />
      </Box>
    );
  }

  return (
    <Box sx={{ 
      width: '100%',
      minHeight: '100%',
      p: { xs: 2, sm: 3, md: 4 }
    }}>
      
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 2,
        mb: 4
      }}>
        <Box>
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            component="h1"
            sx={{ 
              fontWeight: 'bold',
              color: '#1A237E',
              mb: 0.5
            }}
          >
            🛠️ Gestión de Servicios
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Administra el catálogo de servicios de tu empresa
          </Typography>
        </Box>

        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/home/servicios/crear')}
            sx={{
              background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
              borderRadius: 2,
              px: 3,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 'bold',
              boxShadow: '0 4px 15px rgba(26, 35, 126, 0.3)',
              '&:hover': {
                background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
                boxShadow: '0 6px 20px rgba(26, 35, 126, 0.4)',
                transform: 'translateY(-2px)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            Crear Servicio
          </Button>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          {error}
        </Alert>
      )}

      {/* Estadísticas */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ color: '#1A237E', fontWeight: 'bold' }}>
              {estadisticas.total}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Servicios Totales
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ color: '#4CAF50', fontWeight: 'bold' }}>
              {formatPrice(estadisticas.promedio)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Precio Promedio
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ color: '#FF9800', fontWeight: 'bold' }}>
              {formatPrice(estadisticas.masCaroe)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Más Caro
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Card sx={{ borderRadius: 2, textAlign: 'center', p: 2 }}>
            <Typography variant="h4" sx={{ color: '#2196F3', fontWeight: 'bold' }}>
              {formatPrice(estadisticas.masBarato)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Más Barato
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Barra de búsqueda */}
      <Box sx={{ mb: 3 }}>
        <TextField
          variant="outlined"
          fullWidth={isMobile}
          size="small"
          placeholder="Buscar por nombre, descripción, precio..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: '#4FC3F7' }} />
              </InputAdornment>
            ),
          }}
          sx={{ 
            width: isMobile ? '100%' : 400,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover fieldset': {
                borderColor: '#4FC3F7',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#1A237E',
              },
            }
          }}
        />
      </Box>

      {/* Vista móvil - Cards */}
      {isMobile ? (
        <Box>
          {filteredServicios
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((servicio) => (
              <MobileServiceCard key={servicio.id_servicio} servicio={servicio} />
            ))}
          
          {filteredServicios.length > rowsPerPage && (
            <Paper sx={{ mt: 2, borderRadius: 2 }}>
              <TablePagination
                rowsPerPageOptions={[20]}
                component="div"
                count={filteredServicios.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                labelDisplayedRows={({ from, to, count }) =>
                  `${from}-${to} de ${count}`
                }
                labelRowsPerPage="Filas por página:"
              />
            </Paper>
          )}
        </Box>
      ) : (
        /* Vista desktop - Tabla mejorada */
        <Paper sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          border: '1px solid rgba(26, 35, 126, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)'
        }}>
          <TableContainer>
            <Table>
              <TableHead sx={{ 
                background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)' 
              }}>
                <TableRow>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Servicio
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'nombre_servicio'}
                      direction={orderBy === 'nombre_servicio' ? order : 'asc'}
                      onClick={createSortHandler('nombre_servicio')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': { color: 'white !important' }
                      }}
                    >
                      Nombre
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Descripción
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    <TableSortLabel
                      active={orderBy === 'precio'}
                      direction={orderBy === 'precio' ? order : 'asc'}
                      onClick={createSortHandler('precio')}
                      sx={{ 
                        color: 'white !important',
                        '& .MuiTableSortLabel-icon': { color: 'white !important' }
                      }}
                    >
                      Precio
                    </TableSortLabel>
                  </TableCell>
                  <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredServicios
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((servicio, index) => (
                    <TableRow 
                      key={servicio.id_servicio}
                      sx={{ 
                        '&:nth-of-type(even)': { 
                          backgroundColor: 'rgba(26, 35, 126, 0.02)' 
                        },
                        '&:hover': {
                          backgroundColor: 'rgba(79, 195, 247, 0.1)',
                          transform: 'scale(1.01)',
                          transition: 'all 0.2s ease'
                        }
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #4FC3F7 0%, #29B6F6 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1.2rem'
                            }}
                          >
                            {getServiceIcon(servicio)}
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            ID: {servicio.id_servicio}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {servicio.nombre_servicio}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ 
                            maxWidth: 300,
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden'
                          }}
                        >
                          {servicio.descripcion_servicio || 'Sin descripción'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={formatPrice(servicio.precio)}
                          icon={<MoneyIcon sx={{ fontSize: 14 }} />}
                          sx={{ 
                            backgroundColor: 'rgba(76, 175, 80, 0.1)',
                            color: '#4CAF50',
                            fontWeight: 'bold'
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <Tooltip title="Editar servicio">
                            <IconButton
                              size="small"
                              onClick={() => navigate(`/home/servicios/editar/${servicio.id_servicio}`)}
                              sx={{ 
                                bgcolor: 'primary.light', 
                                color: 'white',
                                '&:hover': { bgcolor: 'primary.main' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar servicio">
                            <IconButton
                              size="small"
                              onClick={() => handleDelete(servicio.id_servicio, servicio.nombre_servicio)}
                              sx={{ 
                                bgcolor: 'error.light', 
                                color: 'white',
                                '&:hover': { bgcolor: 'error.main' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[20]}
            component="div"
            count={filteredServicios.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} de ${count}`
            }
            labelRowsPerPage="Filas por página:"
            sx={{
              borderTop: '1px solid rgba(26, 35, 126, 0.1)',
              backgroundColor: 'rgba(26, 35, 126, 0.02)'
            }}
          />
        </Paper>
      )}

      {/* FAB para móvil */}
      {isMobile && (
        <Fab
          color="primary"
          onClick={() => navigate('/home/servicios/crear')}
          sx={{
            position: 'fixed',
            bottom: 20,
            right: 20,
            background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #0D47A1 0%, #1976D2 100%)',
            }
          }}
        >
          <AddIcon />
        </Fab>
      )}

      {/* Mensaje cuando no hay servicios */}
      {filteredServicios.length === 0 && !loading && (
        <Paper sx={{ 
          p: 4, 
          textAlign: 'center', 
          borderRadius: 3,
          border: '2px dashed rgba(26, 35, 126, 0.2)'
        }}>
          <WorkIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {searchTerm ? 'No se encontraron servicios' : 'No hay servicios registrados'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm 
              ? 'Prueba con otros términos de búsqueda'
              : 'Comienza creando tu primer servicio'
            }
          </Typography>
          {!searchTerm && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => navigate('/home/servicios/crear')}
              sx={{
                background: 'linear-gradient(135deg, #1A237E 0%, #3949AB 100%)',
                borderRadius: 2
              }}
            >
              Crear Primer Servicio
            </Button>
          )}
        </Paper>
      )}
    </Box>
  );
}

export default ServiciosAdmin;